
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { apiClient } from '@/lib/apiClient';

interface ProfileData {
  id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  department?: string;
  position?: string;
  phone?: string;
  profile_image?: string;
  email_full_name?: string;
  imap_email?: string;
  imap_username?: string;
  imap_password?: string;
  imap_server?: string;
  imap_port?: string;
  imap_use_ssl?: string;
  smtp_server?: string;
  smtp_port?: string;
  smtp_username?: string;
  smtp_password?: string;
  smtp_use_tls?: string;
  smtp_from_name?: string;
  is_active: boolean;
  is_superuser: boolean;
}

const fetchProfileData = async (userId: number): Promise<ProfileData> => {
  return apiClient.get<ProfileData>(`/rbac/profile/${userId}`);
};

export const useProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: () => fetchProfileData(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'An error occurred') : null;

  const updateProfile = async (updateData: Partial<ProfileData>) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      await apiClient.put(`/rbac/profile/${user.id}`, updateData);

      // Invalidate to refetch
      await queryClient.invalidateQueries({ queryKey: ['profile', user.id] });

      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });
    } catch (err) {
      console.error('Profile update error:', err);
      const errorMessage = err instanceof Error ? err.message : "Failed to update profile";
      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
      throw err instanceof Error ? err : new Error('An error occurred');
    }
  };

  return {
    profile: profile ?? null,
    loading,
    error,
    updateProfile,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['profile', user?.id] }),
  };
};
